// src/services/authService.ts
import { supabase } from '../lib/supabaseClient';
import { USE_MOCK_AUTH, MOCK_JWT_TOKEN } from '../config';

interface GoogleAuthResponse {
  success: boolean;
  user?: {
    id: string;
    email: string;
    name: string;
    avatar_url?: string;
    role: string;
    modules_access: string[];
  };
  session?: {
    token: string;
    expires_at: string;
  };
  error?: string;
}

interface UserProfile {
  id: string;
  email: string;
  name: string;
  avatar_url?: string;
  role: string;
  modules_access: string[];
}

export class AuthService {
  private static SESSION_KEY = 'plexiSession';
  private static sessionCheckInterval: NodeJS.Timeout | null = null;
  
  /**
   * Autoryzacja przez Google OAuth
   */
  static async loginWithGoogle(googleToken: string): Promise<{
    success: boolean;
    user?: UserProfile;
    error?: string;
    message?: string;
  }> {
    try {
      console.log('🔐 Rozpoczynam autoryzację Google...');
      
      // Dekoduj Google ID token żeby pobrać email
      const base64Url = googleToken.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      }).join(''));
      
      const payload = JSON.parse(jsonPayload);
      const email = payload.email;
      
      console.log('📧 Email z Google token:', email);
      
      // Wywołaj backend endpoint do logowania
      const apiUrl = 'https://api.plexisystem.pl'; // ZAWSZE używaj tego URL
      console.log('🔑 Logging in via backend:', `${apiUrl}/api/sessions/login`);
      
      const response = await fetch(`${apiUrl}/api/sessions/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          email,
          googleToken
        })
      });
      
      console.log('📡 Login response:', response.status, response.statusText);
      
      if (!response.ok) {
        const errorData = await response.json();
        return {
          success: false,
          error: errorData.error || 'Błąd logowania',
          message: errorData.message
        };
      }
      
      const data = await response.json();
      console.log('✅ Login successful, user:', data.user.email);
      
      // Zapisz sesję
      this.saveSession(data.user, data.session);
      
      // Ustaw automatyczne odświeżanie
      this.startSessionCheck();
      
      return {
        success: true,
        user: data.user
      };
      
    } catch (error) {
      console.error('❌ Błąd autoryzacji Google:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Nieznany błąd',
      };
    }
  }
  
  /**
   * Zapisz sesję
   */
  private static saveSession(user: UserProfile, session: { token: string; expires_at: string }) {
    console.log('💾 Saving session:', {
      userEmail: user.email,
      hasToken: !!session.token,
      tokenPreview: session.token ? `${session.token.substring(0, 20)}...` : 'NO TOKEN',
      expiresAt: session.expires_at
    });
    
    const sessionData = {
      user,
      session,
      timestamp: new Date().toISOString()
    };
    
    // Zapisz bez szyfrowania - to uprości sprawę
    const sessionString = JSON.stringify(sessionData);
    
    // Używamy localStorage zamiast sessionStorage aby dane przetrwały
    localStorage.setItem(this.SESSION_KEY, sessionString);
    // Duplikuj do sessionStorage dla kompatybilności
    sessionStorage.setItem(this.SESSION_KEY, sessionString);
    
    console.log('✅ Session saved to both localStorage and sessionStorage');
    
    // NIE INTEGRUJEMY Z SUPABASE AUTH - używamy własnego systemu JWT
    console.log('🔒 Using custom JWT auth - Supabase Auth integration skipped');
    
    // Ustaw globalny token dla zgodności
    if (typeof window !== 'undefined' && (window as any).__token !== session.token) {
      (window as any).__token = session.token;
      console.log('🎫 Global token updated');
    }
  }
  
  /**
   * Wyczyść starą sesję jeśli jest uszkodzona
   */
  static clearCorruptedSession() {
    console.log('🧹 Clearing potentially corrupted session');
    sessionStorage.removeItem(this.SESSION_KEY);
    localStorage.removeItem(this.SESSION_KEY);
  }
  
  /**
   * Pobierz aktualną sesję
   */
  static getSession(): { user: UserProfile; session: any } | null {
    try {
      // Sprawdź najpierw localStorage, potem sessionStorage
      let sessionString = localStorage.getItem(this.SESSION_KEY);
      const sessionStorageString = sessionStorage.getItem(this.SESSION_KEY);
      
      // Jeśli localStorage jest pusty ale sessionStorage ma dane, synchronizuj
      if (!sessionString && sessionStorageString) {
        console.log('📋 Synchronizuję sesję z sessionStorage do localStorage');
        localStorage.setItem(this.SESSION_KEY, sessionStorageString);
        sessionString = sessionStorageString;
      }
      
      if (!sessionString) return null;
      
      let sessionData;
      
      // Próbuj najpierw jako plain JSON
      try {
        sessionData = JSON.parse(sessionString);
      } catch (e) {
        // Jeśli to nie działa, może być zaszyfrowane (stara wersja)
        try {
          const decryptedData = this.decrypt(sessionString);
          sessionData = JSON.parse(decryptedData);
        } catch (e2) {
          console.error('❌ Session parsing failed:', e2);
          this.clearCorruptedSession();
          return null;
        }
      }
      
      // Walidacja struktury sesji
      if (!sessionData || !sessionData.user || !sessionData.session) {
        console.error('❌ Invalid session structure');
        this.clearCorruptedSession();
        return null;
      }
      
      // Sprawdź czy sesja ma expires_at
      if (!sessionData.session.expires_at) {
        console.error('❌ Session missing expires_at');
        this.clearCorruptedSession();
        return null;
      }
      
      // Sprawdź czy sesja nie wygasła
      if (new Date(sessionData.session.expires_at) < new Date()) {
        this.logout();
        return null;
      }
      
      return sessionData;
    } catch (error) {
      console.error('❌ Session retrieval failed:', error);
      // Jeśli coś poszło nie tak, wyczyść uszkodzoną sesję
      this.clearCorruptedSession();
      return null;
    }
  }
  
  /**
   * Pobierz aktualny token
   */
  static getToken(): string | null {
    const session = this.getSession();
    return session?.session?.token || null;
  }
  
  /**
   * Sprawdź status sesji
   */
  static async checkSession(): Promise<boolean> {
    const session = this.getSession();
    if (!session) return false;
    
    // Sprawdź na backendzie czy sesja jest ważna
    try {
      const apiUrl = 'https://api.plexisystem.pl'; // ZAWSZE używaj tego URL
      const response = await fetch(`${apiUrl}/api/sessions/verify`, {
        headers: {
          'Authorization': `Bearer ${session.session.token}`
        }
      });
      
      if (!response.ok) {
        this.logout();
        return false;
      }
      
      const data = await response.json();
      return data.valid === true;
      
    } catch (error) {
      console.error('Session check failed:', error);
      // W przypadku błędu sieci, sprawdź lokalnie
      if (new Date(session.session.expires_at) < new Date()) {
        this.logout();
        return false;
      }
      return true;
    }
  }
  
  /**
   * Rozpocznij automatyczne sprawdzanie sesji
   */
  private static startSessionCheck() {
    // Sprawdzaj co 5 minut
    this.sessionCheckInterval = setInterval(async () => {
      const isValid = await this.checkSession();
      if (!isValid) {
        console.log('⏰ Sesja wygasła');
      }
    }, 5 * 60 * 1000);
  }
  
  /**
   * Wyloguj użytkownika
   */
  static async logout() {
    const session = this.getSession();
    
    // Wywołaj backend endpoint do wylogowania
    if (session?.session?.token) {
      try {
        const apiUrl = 'https://api.plexisystem.pl'; // ZAWSZE używaj tego URL
        await fetch(`${apiUrl}/api/sessions/logout`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.session.token}`
          }
        });
      } catch (error) {
        console.error('Logout error:', error);
      }
    }
    
    // Wyczyść lokalną sesję
    sessionStorage.removeItem(this.SESSION_KEY);
    localStorage.removeItem(this.SESSION_KEY);
    
    // Zatrzymaj sprawdzanie sesji
    if (this.sessionCheckInterval) {
      clearInterval(this.sessionCheckInterval);
      this.sessionCheckInterval = null;
    }
    
    // Przekieruj do logowania
    window.location.href = '/login';
  }
  
  /**
   * Sprawdź czy użytkownik jest zalogowany
   */
  static isAuthenticated(): boolean {
    return this.getSession() !== null;
  }
  
  /**
   * Prosty encrypt (w produkcji użyj prawdziwego szyfrowania)
   */
  private static encrypt(text: string): string {
    try {
      // W produkcji użyj Web Crypto API lub biblioteki jak crypto-js
      return btoa(unescape(encodeURIComponent(text)));
    } catch (e) {
      console.error('Encryption failed:', e);
      return text; // Fallback - zwróć plain text
    }
  }
  
  /**
   * Prosty decrypt
   */
  private static decrypt(text: string): string {
    try {
      // W produkcji użyj Web Crypto API
      return decodeURIComponent(escape(atob(text)));
    } catch (e) {
      console.error('Decryption failed:', e);
      return text; // Fallback - zwróć as is
    }
  }
  
  /**
   * Pobierz dane użytkownika
   */
  static getCurrentUser(): UserProfile | null {
    const session = this.getSession();
    return session?.user || null;
  }
  
  /**
   * Sprawdź dostęp do modułu
   */
  static hasModuleAccess(moduleId: string): boolean {
    const user = this.getCurrentUser();
    if (!user) return false;
    
    // Admin ma dostęp do wszystkiego
    if (user.role === 'admin') return true;
    
    // Sprawdź modules_access
    return user.modules_access?.includes(moduleId) || false;
  }
  
  /**
   * Helper do fetch z autoryzacją
   */
  static async fetchWithAuth(url: string, options: RequestInit = {}): Promise<Response> {
    const token = this.getToken();
    
    // Jeśli URL jest względny, dodaj bazowy URL
    if (url.startsWith('/')) {
      const apiUrl = 'https://api.plexisystem.pl'; // ZAWSZE używaj tego URL
      url = apiUrl + url;
    }
    
    const headers: any = {
      ...options.headers,
      'Content-Type': 'application/json'
    };
    
    // Dodaj Authorization tylko jeśli token istnieje
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    
    console.log('🚀 Fetching with auth:', {
      url,
      hasToken: !!token,
      tokenPreview: token ? `${token.substring(0, 20)}...` : 'NO TOKEN'
    });
    
    return fetch(url, {
      ...options,
      headers
    });
  }
}

// Auto-sprawdzanie przy załadowaniu
if (typeof window !== 'undefined') {
  // Opóźnij sprawdzanie sesji o 100ms, aby dać czas na załadowanie
  setTimeout(async () => {
    const isValid = await AuthService.checkSession();
    
    if (isValid) {
      console.log('✅ Session is valid - using backend session management');
      const token = AuthService.getToken();
      if (token) {
        console.log('🔒 JWT token available:', token.substring(0, 20) + '...');
      }
    } else {
      console.log('⚠️ No valid session found');
    }
  }, 100);
}
